import React, { useEffect, useMemo, useState } from "react"
import { Modal, message } from "antd"
import * as com from "../Common"
import {
    closingCostWindowError,
    compensationLimitError,
    feeErrorsFor,
    maxCompensationZeroesPay,
    statesUsedByOtherRows,
    MAX_COMPENSATION_ZERO_CONFIRM,
} from "./lenderConfig"
import "./AddLendersModal.css"

const DEFAULT_LENDER_LOGO = "/images/lendersLogo/DefaultLender_logo.svg"

const RELATIONSHIP_KEYS = Object.keys(com.LenderRelationshhip)

const blankConfig = () => ({
    comp: "1",
    flat: "0",
    min: "",
    max: "",
    minCC: "",
    maxCC: "",
    feeType: "flat",
    fee: "0",
    overrides: [],
    rel: RELATIONSHIP_KEYS[0],
    moreOpen: false,
})

const fmtMoney = (v) => {
    if (v === null || v === undefined || v === "") return ""
    const parts = String(v).replace(/,/g, "").split(".")
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",")
    return parts.join(".")
}

const stripMoney = (v) => String(v).replace(/[^0-9.-]/g, "")

// The draft keeps its own field names; the shared checks read a saved Config.
const asSavedCompensation = (c) => ({
    compensation: c.comp,
    compensationFlatFee: c.flat,
    minimumCompensation: c.min,
    maximumCompensation: c.max,
})

const CheckIcon = () => (
    <svg width="11" height="9" viewBox="0 0 12 10" fill="none">
        <path d="M1 5L4.5 8.5L11 1" stroke="white" strokeWidth="2" />
    </svg>
)

const SearchIcon = () => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
        <circle cx="11" cy="11" r="7" stroke="#71717A" strokeWidth="2" />
        <path d="M20 20L16.5 16.5" stroke="#71717A" strokeWidth="2" strokeLinecap="round" />
    </svg>
)

const CloseIcon = ({ size = 16, color = "#71717A" }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
        <path d="M6 6L18 18M18 6L6 18" stroke={color} strokeWidth="2" strokeLinecap="round" />
    </svg>
)

const PlusIcon = () => (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
        <path d="M12 5V19M5 12H19" stroke="#325CEB" strokeWidth="2" strokeLinecap="round" />
    </svg>
)

const ChevronIcon = ({ color = "#A1A1AA", className = "" }) => (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" className={className}>
        <path d="M9 6L15 12L9 18" stroke={color} strokeWidth="2" strokeLinecap="round" />
    </svg>
)

// One lender's pricing card on step 2.
const LenderConfigCard = ({ title, showLogo, logo, config, stateOptions, onChange, showErrors }) => {
    const patch = (p) => onChange({ ...config, ...p })

    const patchOverride = (i, p) =>
        patch({ overrides: config.overrides.map((o, j) => (j === i ? { ...o, ...p } : o)) })

    const errors = showErrors ? feeErrorsFor(config) : []
    const defaultFeeInvalid = errors.includes("default")
    const closingCostError = showErrors ? closingCostWindowError(config.minCC, config.maxCC) : null

    return (
        <div className="alm-card">
            <div className="alm-card-head">
                {showLogo && <img src={logo || DEFAULT_LENDER_LOGO} alt="" />}
                <span className="alm-card-name">{title}</span>
            </div>

            <div className="alm-grid">
                <div>
                    <div className="alm-field-label">Compensation point</div>
                    <div className="alm-input">
                        <input
                            value={config.comp}
                            onChange={(e) => patch({ comp: e.target.value })}
                        />
                        <span className="alm-affix">%</span>
                    </div>
                </div>
                <div>
                    <div className="alm-field-label">Flat fee</div>
                    <div className="alm-input">
                        <span className="alm-affix">$</span>
                        <input
                            value={fmtMoney(config.flat)}
                            onChange={(e) => patch({ flat: stripMoney(e.target.value) })}
                        />
                    </div>
                </div>
                <div>
                    <div className="alm-field-label">Relationship</div>
                    <select
                        className="alm-select"
                        value={config.rel}
                        onChange={(e) => patch({ rel: e.target.value })}
                    >
                        {RELATIONSHIP_KEYS.map((key) => (
                            <option value={key} key={key}>
                                {com.LenderRelationshhip[key].name}
                            </option>
                        ))}
                    </select>
                </div>
            </div>

            <div style={{ marginTop: 14 }}>
                <div className="alm-field-label">Origination fee</div>
                <div className="alm-fee-box">
                    <div className="alm-fee-row alm-fee-row-default">
                        <span className="alm-chip-neutral">All states</span>
                        <span className="alm-fee-caption">Default fee</span>
                        <select
                            className="alm-fee-type"
                            value={config.feeType}
                            onChange={(e) => patch({ feeType: e.target.value })}
                        >
                            <option value="flat">Flat fee</option>
                            <option value="percentage">Percent of loan</option>
                        </select>
                        <div className={`alm-input alm-fee-amount ${defaultFeeInvalid ? "alm-invalid" : ""}`}>
                            <span className="alm-affix">{config.feeType === "percentage" ? "%" : "$"}</span>
                            <input
                                value={config.feeType === "percentage" ? config.fee : fmtMoney(config.fee)}
                                onChange={(e) => patch({ fee: stripMoney(e.target.value) })}
                            />
                        </div>
                    </div>
                    {defaultFeeInvalid && (
                        <div className="alm-fee-error">Enter an origination fee greater than 0.</div>
                    )}

                    {config.overrides.map((ov, i) => (
                        <div className="alm-fee-override" key={i}>
                            <div className="alm-fee-row" style={{ padding: 0 }}>
                                <div className="alm-state-chips">
                                    {ov.states.map((abbr) => (
                                        <span className="alm-chip-state" key={abbr}>{abbr}</span>
                                    ))}
                                    <span
                                        className={`alm-picker-btn ${ov.pickerOpen ? "alm-on" : ""}`}
                                        onClick={() => patchOverride(i, { pickerOpen: !ov.pickerOpen })}
                                    >
                                        {ov.pickerOpen ? "Done" : ov.states.length ? "Edit states" : "Choose states"}
                                    </span>
                                </div>
                                <select
                                    className="alm-fee-type"
                                    value={ov.type}
                                    onChange={(e) => patchOverride(i, { type: e.target.value })}
                                >
                                    <option value="flat">Flat fee</option>
                                    <option value="percentage">Percent of loan</option>
                                </select>
                                <div className={`alm-input alm-fee-amount ${errors.includes("override-" + i) ? "alm-invalid" : ""}`}>
                                    <span className="alm-affix">{ov.type === "percentage" ? "%" : "$"}</span>
                                    <input
                                        value={ov.type === "percentage" ? ov.amount : fmtMoney(ov.amount)}
                                        onChange={(e) => patchOverride(i, { amount: stripMoney(e.target.value) })}
                                    />
                                </div>
                                <button
                                    type="button"
                                    className="alm-icon-btn"
                                    title="Remove this override"
                                    onClick={() => patch({ overrides: config.overrides.filter((_, j) => j !== i) })}
                                >
                                    <CloseIcon size={13} color="#B13A3A" />
                                </button>
                            </div>
                            {errors.includes("override-" + i) && (
                                <div className="alm-fee-error">Enter a fee greater than 0 for these states.</div>
                            )}
                            {ov.pickerOpen && (
                                <div className="alm-state-picker">
                                    {(() => {
                                        // a state can only carry one fee, so ones claimed by
                                        // another override are not selectable here
                                        const takenStates = statesUsedByOtherRows(config.overrides, i)
                                        return stateOptions.map((st) => {
                                        const on = ov.states.includes(st.value)
                                        const taken = !on && takenStates.has(st.value)
                                        return (
                                            <span
                                                key={st.value}
                                                title={taken ? st.label + " — already used by another state-specific fee" : st.label}
                                                className={`alm-state-option ${on ? "alm-on" : ""} ${taken ? "alm-taken" : ""}`}
                                                onClick={() => {
                                                    if (taken) return
                                                    patchOverride(i, {
                                                        states: on
                                                            ? ov.states.filter((x) => x !== st.value)
                                                            : [...ov.states, st.value],
                                                    })
                                                }}
                                            >
                                                {st.value}
                                            </span>
                                        )
                                        })
                                    })()}
                                </div>
                            )}
                        </div>
                    ))}

                    <button
                        type="button"
                        className="alm-add-override"
                        onClick={() =>
                            patch({
                                overrides: [
                                    ...config.overrides,
                                    { states: [], type: "flat", amount: "0", pickerOpen: true },
                                ],
                            })
                        }
                    >
                        <PlusIcon />
                        Add state-specific fee
                    </button>
                </div>
            </div>

            <button
                type="button"
                className="alm-more-toggle"
                onClick={() => patch({ moreOpen: !config.moreOpen })}
            >
                <ChevronIcon
                    color="#325CEB"
                    className={`alm-more-arrow ${config.moreOpen ? "alm-open" : ""}`}
                />
                {config.moreOpen
                    ? "Hide compensation & closing cost limits"
                    : "Compensation & closing cost limits"}
            </button>

            {config.moreOpen && (
                <div className="alm-grid" style={{ marginTop: 12 }}>
                    <div>
                        <div className="alm-field-label">Min compensation</div>
                        <div className="alm-input">
                            <span className="alm-affix">$</span>
                            <input
                                placeholder="None"
                                value={fmtMoney(config.min)}
                                onChange={(e) => patch({ min: stripMoney(e.target.value) })}
                            />
                        </div>
                    </div>
                    <div>
                        <div className="alm-field-label">Max compensation</div>
                        <div className="alm-input">
                            <span className="alm-affix">$</span>
                            <input
                                placeholder="None"
                                value={fmtMoney(config.max)}
                                onChange={(e) => patch({ max: stripMoney(e.target.value) })}
                            />
                        </div>
                    </div>
                    <div>
                        <div className="alm-field-label">Min closing cost</div>
                        <div className={`alm-input ${closingCostError ? "alm-invalid" : ""}`}>
                            <input
                                placeholder="No limit"
                                value={config.minCC}
                                onChange={(e) => patch({ minCC: e.target.value })}
                            />
                            <span className="alm-affix">%</span>
                        </div>
                    </div>
                    <div>
                        <div className="alm-field-label">Max closing cost</div>
                        <div className={`alm-input ${closingCostError ? "alm-invalid" : ""}`}>
                            <input
                                placeholder="No limit"
                                value={config.maxCC}
                                onChange={(e) => patch({ maxCC: e.target.value })}
                            />
                            <span className="alm-affix">%</span>
                        </div>
                    </div>
                </div>
            )}
            {/* outside the fold, so the reason shows even while the limits are hidden */}
            {closingCostError && <div className="alm-cc-error">{closingCostError}</div>}
        </div>
    )
}

/**
 * Two step "Add lenders" flow:
 *   1. pick lenders from the catalog
 *   2. set pricing for them and save — saving happens here, so there is no
 *      separate "now go press Save on each row" step afterwards.
 *
 * `onSave` receives [{ name, config }] where config holds the raw form values;
 * the caller maps them onto the lender Config shape.
 */
const AddLendersModal = ({
    open,
    onClose,
    catalog = [],
    stateOptions = [],
    logoMap = {},
    saving = false,
    onSave,
    footerLeft = null,
}) => {
    const [step, setStep] = useState(1)
    const [search, setSearch] = useState("")
    const [selected, setSelected] = useState([])
    const [configs, setConfigs] = useState({})
    const [shared, setShared] = useState(blankConfig)
    const [applyAll, setApplyAll] = useState(false)
    const [showErrors, setShowErrors] = useState(false)

    // Reset every time the modal is opened so a cancelled run never leaks into the next one.
    useEffect(() => {
        if (open) {
            setStep(1)
            setSearch("")
            setSelected([])
            setConfigs({})
            setShared(blankConfig())
            setApplyAll(false)
            setShowErrors(false)
        }
    }, [open])

    const filtered = useMemo(() => {
        const keyword = search.trim().toLowerCase()
        return catalog.filter((x) => x.name.toLowerCase().includes(keyword))
    }, [catalog, search])

    const toggleLender = (name) =>
        setSelected((prev) =>
            prev.includes(name) ? prev.filter((x) => x !== name) : [...prev, name]
        )

    const goToStep2 = () => {
        if (selected.length === 0) return
        const next = {}
        selected.forEach((name) => {
            next[name] = configs[name] || blankConfig()
        })
        setConfigs(next)
        setStep(2)
    }

    const sharedMode = applyAll && selected.length > 1

    const handleSave = () => {
        const payload = selected.map((name) => ({
            name,
            config: sharedMode ? shared : configs[name],
        }))
        const invalid = payload.filter((p) => feeErrorsFor(p.config).length > 0)
        if (invalid.length > 0) {
            setShowErrors(true)
            message.error(
                sharedMode || invalid.length === selected.length
                    ? "Enter an origination fee greater than 0 before saving."
                    : "Enter an origination fee greater than 0 for " +
                      invalid.map((p) => p.name).join(", ") + "."
            )
            return
        }
        const badWindows = payload.filter((p) => closingCostWindowError(p.config.minCC, p.config.maxCC) !== null)
        if (badWindows.length > 0) {
            setShowErrors(true)
            message.error(
                sharedMode || badWindows.length === selected.length
                    ? "Fix the closing cost limits before saving."
                    : "Fix the closing cost limits for " + badWindows.map((p) => p.name).join(", ") + "."
            )
            return
        }
        const badComp = payload.filter((p) => compensationLimitError(p.config.min, p.config.max) !== null)
        if (badComp.length > 0) {
            const first = badComp[0]
            message.error(
                (sharedMode || badComp.length === selected.length ? "" : first.name + ": ") +
                    compensationLimitError(first.config.min, first.config.max)
            )
            return
        }
        const zeroMax = payload.filter((p) => maxCompensationZeroesPay(asSavedCompensation(p.config)))
        if (zeroMax.length > 0) {
            Modal.confirm({
                title: "Max compensation is $0" +
                    (sharedMode || zeroMax.length === selected.length ? "" : " for " + zeroMax.map((p) => p.name).join(", ")),
                content: MAX_COMPENSATION_ZERO_CONFIRM,
                okText: "Save with $0",
                cancelText: "Go back",
                onOk: () => onSave(payload),
            })
            return
        }
        onSave(payload)
    }

    const logoFor = (name) => logoMap[name] || DEFAULT_LENDER_LOGO

    return (
        <Modal
            open={open}
            onCancel={onClose}
            footer={null}
            closable={false}
            destroyOnClose
            width={880}
            centered
            className="addLendersModal"
            maskClosable={!saving}
        >
            <div className="alm-header">
                <div>
                    <div className="alm-title">Add lenders</div>
                    <div className="alm-steps">
                        <span className={step === 1 ? "alm-step-current" : ""}>1. Select lenders</span>
                        <ChevronIcon />
                        <span className={step === 2 ? "alm-step-current" : ""}>2. Set pricing &amp; save</span>
                    </div>
                </div>
                <div className="alm-close" onClick={onClose}>
                    <CloseIcon />
                </div>
            </div>

            {step === 1 ? (
                <>
                    <div className="alm-body">
                        <div className="alm-search">
                            <SearchIcon />
                            <input
                                placeholder="Search lender name"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                            />
                        </div>
                        {filtered.length === 0 ? (
                            <div className="alm-empty">No lenders match your search</div>
                        ) : (
                            <div className="alm-catalog">
                                {filtered.map((x) => {
                                    const on = selected.includes(x.name)
                                    return (
                                        <div
                                            key={x.id}
                                            className={`alm-catalog-item ${on ? "alm-selected" : ""}`}
                                            onClick={() => toggleLender(x.name)}
                                        >
                                            <div className={`alm-checkbox ${on ? "alm-checked" : ""}`}>
                                                {on && <CheckIcon />}
                                            </div>
                                            <img src={logoFor(x.name)} alt="" />
                                            <div className="alm-catalog-name">{x.name}</div>
                                        </div>
                                    )
                                })}
                            </div>
                        )}
                    </div>
                    <div className="alm-footer">
                        <div>{footerLeft}</div>
                        <div className="alm-footer-right">
                            {selected.length > 0 && (
                                <span className="alm-selected-count">{selected.length} selected</span>
                            )}
                            <button
                                type="button"
                                className="alm-primary-btn"
                                style={{ height: 38, padding: "0 20px" }}
                                disabled={selected.length === 0}
                                onClick={goToStep2}
                            >
                                Continue
                            </button>
                        </div>
                    </div>
                </>
            ) : (
                <>
                    <div className="alm-body">
                        <div className="alm-hint">
                            Set everything up here — pricing, state-specific origination fees, and limits.
                            You can still edit any lender later.
                        </div>

                        {selected.length > 1 && (
                            <div
                                className={`alm-apply-all ${applyAll ? "alm-on" : ""}`}
                                onClick={() => setApplyAll(!applyAll)}
                            >
                                <div className={`alm-checkbox ${applyAll ? "alm-checked" : ""}`}>
                                    {applyAll && <CheckIcon />}
                                </div>
                                <div style={{ flex: 1 }}>
                                    <div className="alm-apply-all-title">
                                        Use the same settings for all {selected.length} lenders
                                    </div>
                                    <div className="alm-apply-all-sub">
                                        Configure once and apply everywhere — uncheck to set each lender individually
                                    </div>
                                </div>
                            </div>
                        )}

                        <div className="alm-cards">
                            {sharedMode ? (
                                <LenderConfigCard
                                    title={`All ${selected.length} lenders`}
                                    showLogo={false}
                                    config={shared}
                                    stateOptions={stateOptions}
                                    onChange={setShared}
                                    showErrors={showErrors}
                                />
                            ) : (
                                selected.map((name) => (
                                    <LenderConfigCard
                                        key={name}
                                        title={name}
                                        showLogo
                                        logo={logoFor(name)}
                                        config={configs[name] || blankConfig()}
                                        stateOptions={stateOptions}
                                        showErrors={showErrors}
                                        onChange={(next) =>
                                            setConfigs((prev) => ({ ...prev, [name]: next }))
                                        }
                                    />
                                ))
                            )}
                        </div>
                    </div>
                    <div className="alm-footer">
                        <button type="button" className="alm-link-btn" onClick={() => { setShowErrors(false); setStep(1) }}>
                            Back
                        </button>
                        <button
                            type="button"
                            className="alm-primary-btn"
                            disabled={saving}
                            onClick={handleSave}
                        >
                            {saving
                                ? "Saving…"
                                : `Save & add ${selected.length} lender${selected.length === 1 ? "" : "s"}`}
                        </button>
                    </div>
                </>
            )}
        </Modal>
    )
}

export default AddLendersModal
